const DEFAULT_IMAGE_QUALITY = 76;

/** Vite dev apps that render blocks but do not host studio-shell's /api/site-image. */
const IMAGE_PROXY_BYPASS_PORTS = new Set(['5173', '5190']);

/**
 * Studio-shell origin that hosts `/api/site-image` and `/uploads/...`.
 * Required in production sandbox (Cloudflare Pages) — relative media paths
 * resolve to the sandbox host and 404.
 */
let configuredProxyOrigin: string | null = null;

export function configureSiteImageProxyOrigin(origin: string | null | undefined): void {
  const trimmed = origin?.trim().replace(/\/+$/, '') ?? '';
  configuredProxyOrigin = trimmed || null;
}

export function getConfiguredSiteImageProxyOrigin(): string | null {
  return configuredProxyOrigin;
}

function shouldBypassProxyInBrowser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return IMAGE_PROXY_BYPASS_PORTS.has(window.location.port);
}

function resolveProxyPrefix(): string {
  if (!configuredProxyOrigin) {
    return '';
  }
  if (typeof window !== 'undefined') {
    try {
      if (window.location.origin === configuredProxyOrigin) {
        return '';
      }
    } catch {
      // ignore invalid location in non-browser test envs
    }
  }
  return configuredProxyOrigin;
}

const UPLOAD_PATH_RE =
  /^\/uploads\/[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.[a-z0-9]+$/i;

/**
 * Platform stock media under `public/platform/defaults/` (synced to R2):
 * logos/, heroes/{vertical}/, facilities/{vertical}/, menu/{vertical}/ dishes,
 * sample Speisekarte PDF, plus legacy flat nav-mark paths.
 */
const PLATFORM_DEFAULT_MEDIA_PATH_RE =
  /^\/platform\/defaults\/(?:logos\/[^/]+|heroes\/[a-z0-9_-]+\/[^/]+|facilities\/[a-z0-9_-]+\/[^/]+|menu\/[a-z0-9_-]+\/[^/]+|menu-sample\.pdf|(?:nav-mark-|nav-logo)[^/]*)$/i;

/** Same-origin sample PDF for Static_Menu_Pdf_Card seeds (W3C dummy blocks framing). */
const PLATFORM_DEFAULT_MENU_SAMPLE_PDF_PATH = '/platform/defaults/menu-sample.pdf';

/** Legacy bootstrap URL — CSP `frame-ancestors` only allows w3.org, so iframes stay blank. */
const LEGACY_NON_EMBEDDABLE_MENU_PDF_RE =
  /^https:\/\/www\.w3\.org\/WAI\/ER\/tests\/xhtml\/testfiles\/resources\/pdf\/dummy\.pdf\/?$/i;

function toAbsoluteOrRelativePathname(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }
  try {
    if (trimmed.includes('://')) {
      return new URL(trimmed).pathname;
    }
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  } catch {
    return null;
  }
}

/**
 * Normalize any absolute/relative tenant upload URL to path-only `/uploads/{projectId}/{file}`.
 * Returns null for stock/external URLs.
 */
export function toPublicUploadPath(url: string): string | null {
  const pathname = toAbsoluteOrRelativePathname(url);
  return pathname && UPLOAD_PATH_RE.test(pathname) ? pathname : null;
}

/**
 * Normalize platform default media URLs to path-only `/platform/defaults/…`.
 * Returns null for non-stock URLs.
 */
function toPlatformDefaultMediaPath(url: string): string | null {
  const pathname = toAbsoluteOrRelativePathname(url);
  return pathname && PLATFORM_DEFAULT_MEDIA_PATH_RE.test(pathname) ? pathname : null;
}

/** Path-only form for uploads or platform defaults (strips CDN/shell origins). */
export function toCanonicalMediaPath(url: string): string | null {
  return toPublicUploadPath(url) ?? toPlatformDefaultMediaPath(url);
}

/**
 * User-facing / stored URL: path-only for platform uploads & defaults.
 * External URLs (Unsplash, etc.) pass through unchanged.
 * Never returns `cdn.*` for owned media.
 * Rewrites the legacy non-embeddable W3C dummy PDF to the platform sample.
 */
export function canonicalizeMediaUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (LEGACY_NON_EMBEDDABLE_MENU_PDF_RE.test(trimmed)) {
    return PLATFORM_DEFAULT_MENU_SAMPLE_PDF_PATH;
  }
  return toCanonicalMediaPath(trimmed) ?? trimmed;
}

/**
 * Absolute URL for loading path-only media when same-origin relative is not enough
 * (sandbox iframe → studio-shell). Uses the configured **shell** origin — never the
 * artifact CDN (`cdn.huba.info`). On shell/tenant same-origin, returns path-only.
 */
export function resolveAbsoluteMediaUrl(
  url: string,
  options?: { shellOrigin?: string | null },
): string {
  const path = toCanonicalMediaPath(url);
  if (!path) {
    return url.trim();
  }
  const origin =
    (options?.shellOrigin ?? configuredProxyOrigin)?.trim().replace(/\/$/, '') || '';
  if (!origin) {
    return path;
  }
  if (typeof window !== 'undefined') {
    try {
      if (window.location.origin === origin) {
        return path;
      }
    } catch {
      // ignore invalid location in non-browser test envs
    }
  }
  return `${origin}${path}`;
}

/**
 * Resolve upload / platform-default media for display: same-origin relative on
 * tenant/shell, shell-absolute inside the sandbox iframe.
 */
export function resolvePublicUploadUrl(url: string): string {
  return resolveAbsoluteMediaUrl(url);
}

export function buildSiteImageProxyUrl(
  sourceUrl: string,
  width: number,
  quality: number = DEFAULT_IMAGE_QUALITY,
): string {
  const trimmed = sourceUrl.trim();
  if (!trimmed) {
    return trimmed;
  }

  // Owner uploads + platform defaults: path-only / shell-absolute (never CDN; skip image proxy).
  if (toCanonicalMediaPath(trimmed)) {
    return resolveAbsoluteMediaUrl(trimmed);
  }

  if (shouldBypassProxyInBrowser()) {
    return trimmed;
  }

  const prefix = resolveProxyPrefix();

  if (trimmed.startsWith('/api/site-image?')) {
    return prefix ? `${prefix}${trimmed}` : trimmed;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const absolute = new URL(trimmed);
      if (absolute.pathname === '/api/site-image' || absolute.pathname.endsWith('/api/site-image')) {
        return trimmed;
      }
    } catch {
      // fall through to wrap
    }
  }

  const params = new URLSearchParams({
    url: trimmed,
    w: String(Math.max(320, Math.round(width))),
    q: String(Math.min(90, Math.max(45, Math.round(quality)))),
  });
  const path = `/api/site-image?${params.toString()}`;
  return prefix ? `${prefix}${path}` : path;
}

export function buildSiteImageSrcSet(
  sourceUrl: string,
  widths: readonly number[],
  quality: number = DEFAULT_IMAGE_QUALITY,
): string {
  const unique = [...new Set(widths)]
    .map((w) => Math.round(w))
    .filter((w) => Number.isFinite(w) && w > 0)
    .sort((a, b) => a - b);
  return unique.map((w) => `${buildSiteImageProxyUrl(sourceUrl, w, quality)} ${w}w`).join(', ');
}
